import React, { useCallback, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    TextInput,
} from 'grommet';
import {
    API,
    resolveSortField,
    RowsPerPageSelect,
    usePaginatedFetch,
    usePaginatedSort,
    Utils,
} from '@enrich/shared-lib';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';

const SEARCH_DEBOUNCE_MS = 400;

export const ClosedJobs = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'updatedAt', direction: 'desc' },
    });

    // The input stays instant; only the debounced value drives a refetch.
    // A new term also drops back to page 1, since the current offset may
    // not exist in the narrowed set.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    const fetchPage = useCallback(
        () => loadJobs({ page, pageSize, sort, search: appliedSearchTerm }),
        [page, pageSize, sort, appliedSearchTerm],
    );
    const { items: jobs } = usePaginatedFetch({
        fetchPage,
        applyPagination,
        transform: Utils.deepFlatten,
    });

    const dataColumns = [
        {
            header: 'Job Title',
            property: 'title',
            render: (job) => {
                return (
                    <Anchor
                        key={job.id}
                        href={`/jobs/${job.id}`}
                        label={job.title}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Company',
            property: 'company.name',
            render: (job) => {
                if (!job.company) return 'N/A';
                return job.company.name;
            },
            sortable: true,
        },
        {
            header: 'Seats',
            property: 'seats',
            sortable: true,
        },
        {
            header: 'Offers Accepted',
            property: 'offersAccepted',
            sortable: true,
            render: (job) => {
                const matches = job.candidacies ?? [];
                if (matches.length === 0) return 0;
                return (
                    matches.filter((x) => x.status === 'offer_accepted')
                        .length || 0
                );
            },
        },
        {
            header: 'Matches',
            property: 'matchCount',
            sortable: true,
            render: (job) => {
                return job.candidacies?.length || 0;
            },
        },
        {
            header: 'Last Updated',
            property: 'updatedAt',
            sortable: true,
            render: (job) => {
                return new Date(job.updatedAt).toLocaleString(undefined, {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                });
            },
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="2">Closed Jobs</Heading>
                        <Box width="medium" justify="center">
                            <TextInput
                                a11yTitle="Search closed jobs"
                                placeholder="Search by job or company name"
                                value={searchTerm}
                                onChange={(event) => {
                                    setSearchTerm(event.target.value);
                                    applySearchTerm(event.target.value);
                                }}
                            />
                        </Box>
                    </Box>
                    <Box>
                        <DataTable
                            primaryKey="id"
                            sort={{ ...sort, external: true }}
                            onSort={handleSort}
                            data={jobs}
                            columns={dataColumns}
                        />
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    title: 'title',
    'company.name': 'company.name',
    seats: 'seats',
    updatedAt: 'updatedAt',
    offersAccepted: 'offersAcceptedCount',
    matchCount: 'matchCount',
};

const buildFilters = (search) => {
    const filters = { status: 'closed' };
    const term = search?.trim();
    if (!term) return filters;

    filters.$or = [
        { title: { $containsi: term } },
        { company: { name: { $containsi: term } } },
    ];

    return filters;
};

const loadJobs = async ({ page, pageSize, sort, search } = {}) => {
    const query = QueryString.stringify(
        {
            filters: buildFilters(search),
            populate: {
                company: {
                    fields: ['id', 'name'],
                },
                candidacies: {
                    filters: {
                        status: {
                            $in: [
                                'interested',
                                'applied',
                                'assessment',
                                'interview',
                                'second_interview',
                                'offer_received',
                                'offer_accepted',
                            ],
                        },
                    },
                    fields: ['id', 'status'],
                },
            },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: [
                `${resolveSortField(sort, SORT_FIELD_MAP, 'updatedAt')}:${
                    sort?.direction ?? 'desc'
                }`,
            ],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};
